import React, { useEffect, useRef, useState } from "react";
import i18n from "@/utils/i18n";
import { useTranslation } from "react-i18next";
import { Download, Close, Certificate } from "@/components/icons";
import Modal from "@/components/ui/modal";
import { generateAndDownloadCertificatePDF } from "@/features/certificates";
import { clientExamsApi } from "../../services/client-exams.api";
import { clientCertificatesApi } from "@/features/client-certificates/services/client-certificates.api";
import styles from "./EnrollmentCertificateViewModal.module.scss";

export interface EnrollmentCertificateViewModalProps {
  educationId?: string | undefined;
  enrollmentId?: string | undefined;
  isOpen: boolean;
  onClose: () => void;
  /** Başlık (liste sayfasından açıldığında) */
  titleOverride?: string;
  /** İndir: HTML yokken (henüz yüklenmediyse) backend PDF; yoksa kullanılmaz, modal HTML→PDF yapar */
  onDownload?: (enrollmentId: string) => void;
}

const EnrollmentCertificateViewModal: React.FC<EnrollmentCertificateViewModalProps> = ({
  educationId,
  enrollmentId,
  isOpen,
  onClose,
  onDownload,
  titleOverride,
}) => {
  const { t } = useTranslation();
  const htmlContainerRef = useRef<HTMLDivElement>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [title, setTitle] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  useEffect(() => {
    if (!isOpen || !enrollmentId) {
      setHtml(null);
      setTitle("");
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const displayTitle = titleOverride || t("clientExams.certificateTitle");

    if (educationId) {
      clientExamsApi
        .getEnrollmentCertificate(educationId, enrollmentId)
        .then((data) => {
          if (!cancelled) {
            setTitle(data.certificateName || data.education?.name || displayTitle);
            setHtml(data.parsedHtml ?? null);
          }
        })
        .catch((err) => {
          if (!cancelled) {
            setError(err?.message || t("clientExams.certificateLoadError"));
            setHtml(null);
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    } else {
      clientCertificatesApi
        .getCertificateHtml(enrollmentId)
        .then((content) => {
          if (!cancelled) {
            setTitle(displayTitle);
            setHtml(content ?? null);
            if (content === null || content === undefined) {
              setError(t("clientExams.certificateLoadError"));
            }
          }
        })
        .catch((err) => {
          if (!cancelled) {
            setError(err?.message || t("clientExams.certificateLoadError"));
            setHtml(null);
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [isOpen, educationId, enrollmentId, titleOverride, t]);

  const handleDownload = async () => {
    if (htmlContainerRef.current && html) {
      setIsGeneratingPdf(true);
      try {
        const filename = (title || "sertifika").replace(/[^\w\s-]/gi, "").replace(/\s+/g, "-") || "sertifika";
        await generateAndDownloadCertificatePDF(htmlContainerRef.current, filename);
      } catch (err) {
        console.error(i18n.t("clientCertificateView.pdfFailed"), err);
      } finally {
        setIsGeneratingPdf(false);
      }
      return;
    }
    if (enrollmentId && onDownload) {
      onDownload(enrollmentId);
    } else if (enrollmentId) {
      clientCertificatesApi.downloadCertificatePdf(enrollmentId);
    }
  };

  const customHeader = (
    <div className={styles.header}>
      <div className={styles.headerLeft}>
        <Certificate width={24} height={24} className={styles.headerIcon} />
        <h3 className={styles.headerTitle}>{title || t("clientExams.certificateTitle")}</h3>
      </div>
      <div className={styles.headerActions}>
        {(enrollmentId || html) && (
          <button
            type="button"
            className={styles.headerBtn}
            onClick={handleDownload}
            disabled={isGeneratingPdf}
            aria-label={t("clientCertificates.card.download")}
          >
            {isGeneratingPdf ? (
              <span className={styles.pdfGenerating}>{t("certificates.create.form.generating")}</span>
            ) : (
              <Download width={24} height={24} />
            )}
          </button>
        )}
        <button
          type="button"
          className={styles.headerBtn}
          onClick={onClose}
          aria-label={t("common.close")}
        >
          <Close width={24} height={24} color="#505766" />
        </button>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      customHeader={customHeader}
      customFooter={null}
      size="xl"
    >
      <div className={styles.body}>
        {loading ? (
          <div className={styles.loading}>
            {t("clientCertificates.loading")}
          </div>
        ) : error ? (
          <div className={styles.error}>{error}</div>
        ) : html ? (
          <div
            ref={htmlContainerRef}
            className={styles.htmlContainer}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          <div className={styles.loading}>
            {t("clientExams.noCertificate")}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default EnrollmentCertificateViewModal;
